/**
 * Money on the forex order screens. Core sends integer paise as strings; they are
 * compared as strings or BigInt and turned into rupees only to be displayed.
 */

/** Paise (string, number or bigint) as "₹1,69,242.10", Indian grouping, always two decimals. */
export function formatInrMinor(minor: string | number | bigint | null | undefined): string {
  const raw = minor === null || minor === undefined || minor === "" ? "0" : String(minor);
  if (!/^-?\d+$/.test(raw)) return "₹0.00";
  const negative = raw.startsWith("-");
  const digits = raw.replace("-", "").padStart(3, "0");
  const whole = digits.slice(0, -2);
  const paise = digits.slice(-2);
  const grouped = new Intl.NumberFormat("en-IN").format(BigInt(whole));
  return `${negative ? "-" : ""}₹${grouped}.${paise}`;
}

/** Rupees as shown on screen (a number already worked out by core), "₹1,69,242.10". */
export function formatInr(rupees: number | string | null | undefined): string {
  const n = Number(rupees ?? 0);
  if (!Number.isFinite(n)) return "₹0.00";
  return formatInrMinor(String(Math.round(n * 100)));
}

/** True when paise strings are zero or less, without any float. */
export const isZeroMinor = (minor: string | null | undefined): boolean => {
  try {
    return BigInt(minor ?? "0") <= 0n;
  } catch {
    return true;
  }
};

export const isPositiveMinor = (minor: string | null | undefined): boolean => !isZeroMinor(minor);

/** Paise to a plain rupee string for an input field, "1690.50". */
export function minorToInput(minor: string | null | undefined): string {
  const raw = String(minor ?? "0");
  if (!/^\d+$/.test(raw)) return "0.00";
  const digits = raw.padStart(3, "0");
  return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

/** What a person typed ("1500", "1500.5") as paise, or null when it is not an amount with at most two decimals. */
export function inputToMinor(text: string): bigint | null {
  const t = text.trim();
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  return BigInt(m[1]) * 100n + BigInt((m[2] ?? "").padEnd(2, "0") || "0");
}
